import { useEffect, useState } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  AppShell, Burger, Group, Text, Box, ActionIcon, ScrollArea, Stack,
  UnstyledButton, Tooltip, useMantineColorScheme, useComputedColorScheme,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { Compass, Sun, Moon, PanelLeft } from 'lucide-react';

import { NAV } from './components/shell/nav';
import { ClientSwitcher } from './components/shell/ClientSwitcher';
import { EpochSwitcher } from './components/shell/EpochSwitcher';
import { epochsApi, setFallbackClientId } from './api';
import { useClientScope } from './hooks/useClientScope';
import { eyebrowStyle } from './theme';

import DashboardPage from './pages/DashboardPage';
import GamesPage from './pages/GamesPage';
import GameDetailPage from './pages/GameDetailPage';
import MembersPage from './pages/MembersPage';
import GroupsPage from './pages/GroupsPage';
import StatsPage from './pages/StatsPage';
import QrCodesPage from './pages/QrCodesPage';
import DevicesPage from './pages/DevicesPage';
import EpochsPage from './pages/EpochsPage';
import GameTypesPage from './pages/GameTypesPage';

const RAIL_WIDE = 244;
const RAIL_NARROW = 66;

function ThemeToggle() {
  const { setColorScheme } = useMantineColorScheme();
  const scheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const dark = scheme === 'dark';
  return (
    <Tooltip label={dark ? 'Světlý režim' : 'Tmavý režim'}>
      <ActionIcon
        variant="default"
        size="lg"
        aria-label="Přepnout světlý a tmavý režim"
        onClick={() => setColorScheme(dark ? 'light' : 'dark')}
      >
        {dark ? <Sun size={16} /> : <Moon size={16} />}
      </ActionIcon>
    </Tooltip>
  );
}

export default function App() {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);
  const [collapsed, setCollapsed] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 48em)');

  const { data: epochs } = useQuery({ queryKey: ['epochs'], queryFn: epochsApi.getAll });
  const activeEpoch = epochs?.find((e) => e.is_active === 1);

  // Endpointy párování vyžadují X-Client-Id; u klientského klíče ho web nikde jinde
  // nevidí než v epoše. U admina rozhoduje výslovná volba organizace (useClientScope),
  // proto se tenhle fallback nesmí uplatnit — poslal by náhodného tenanta.
  const { isAdmin } = useClientScope();
  const ownClientId = (activeEpoch ?? epochs?.[0])?.client_id ?? null;
  useEffect(() => {
    if (!isAdmin) setFallbackClientId(ownClientId);
  }, [isAdmin, ownClientId]);

  const railWidth = collapsed && isDesktop ? RAIL_NARROW : RAIL_WIDE;
  const showLabels = !(collapsed && isDesktop);

  return (
    <AppShell
      layout="alt"
      header={{ height: 52 }}
      navbar={{
        width: railWidth,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened },
      }}
      padding="md"
    >
      <AppShell.Header bg="var(--mantine-color-default)">
        <Group h="100%" px="md" gap="sm" wrap="nowrap">
          <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text style={eyebrowStyle} c="dimmed">
              {t(sectionOf(location.pathname).titleKey, sectionOf(location.pathname).fallback)}
            </Text>
            <Text fw={700} fz="lg" lh={1.2} truncate>
              {t(titleOf(location.pathname).labelKey, titleOf(location.pathname).fallback)}
            </Text>
          </Box>
          <ClientSwitcher />
        </Group>
      </AppShell.Header>

      <AppShell.Navbar bg="var(--mantine-color-default)">
        <Group h={52} px="md" gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
          <Box
            w={27}
            h={27}
            style={(th) => ({
              flex: '0 0 27px',
              borderRadius: th.radius.sm,
              background: 'var(--mantine-primary-color-filled)',
              color: 'var(--mantine-color-white)',
              display: 'grid',
              placeItems: 'center',
            })}
          >
            <Compass size={16} />
          </Box>
          {showLabels && (
            <Box style={{ minWidth: 0 }}>
              <Text fw={700} fz="sm" lh={1.25} truncate>Hvozdici</Text>
              <Text style={eyebrowStyle} c="dimmed" lh={1.2}>Základna</Text>
            </Box>
          )}
        </Group>

        <AppShell.Section grow component={ScrollArea} px="xs" py="sm">
          <Stack gap={1}>
            {NAV.map((section) => (
              <Box key={section.titleKey}>
                {showLabels && (
                  <Text style={eyebrowStyle} c="dimmed" px="xs" pt="sm" pb={4}>
                    {t(section.titleKey, section.fallback)}
                  </Text>
                )}
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const link = (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={closeMobile}
                      style={{ textDecoration: 'none' }}
                    >
                      {({ isActive }) => (
                        <UnstyledButton
                          component="div"
                          px={9}
                          py={6}
                          w="100%"
                          style={(th) => ({
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            justifyContent: showLabels ? 'flex-start' : 'center',
                            borderRadius: th.radius.sm,
                            fontSize: 13.5,
                            fontWeight: isActive ? 650 : 550,
                            color: isActive
                              ? 'var(--mantine-primary-color-filled)'
                              : 'var(--mantine-color-dimmed)',
                            background: isActive
                              ? 'var(--mantine-primary-color-light)'
                              : 'transparent',
                          })}
                        >
                          <Icon size={16} style={{ flex: '0 0 16px' }} />
                          {showLabels && <span>{t(item.labelKey, item.fallback)}</span>}
                        </UnstyledButton>
                      )}
                    </NavLink>
                  );
                  return showLabels ? (
                    link
                  ) : (
                    <Tooltip key={item.to} label={t(item.labelKey, item.fallback)} position="right">
                      <Box>{link}</Box>
                    </Tooltip>
                  );
                })}
              </Box>
            ))}
          </Stack>
        </AppShell.Section>

        <AppShell.Section
          p="xs"
          style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
        >
          <EpochSwitcher compact={!showLabels} />
          <Group gap={6} wrap="nowrap" mt={6}>
            <ThemeToggle />
            <Tooltip label={collapsed ? 'Rozbalit menu' : 'Sbalit menu'}>
              <ActionIcon
                variant="default"
                size="lg"
                visibleFrom="sm"
                aria-label="Sbalit menu"
                onClick={() => setCollapsed((c) => !c)}
              >
                <PanelLeft size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/hry" element={<GamesPage />} />
          <Route path="/hry/:uuid" element={<GameDetailPage />} />
          <Route path="/clenove" element={<MembersPage />} />
          <Route path="/skupiny" element={<GroupsPage />} />
          <Route path="/statistiky" element={<StatsPage />} />
          <Route path="/qr-kody" element={<QrCodesPage />} />
          <Route path="/zarizeni" element={<DevicesPage />} />
          <Route path="/epochy" element={<EpochsPage />} />
          <Route path="/herni-mody" element={<GameTypesPage />} />
          <Route path="*" element={<DashboardPage />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}

/** Titulek a nadtitulek v hlavičce se odvozují z cesty, ať je nemusí řešit každá stránka. */
function titleOf(pathname: string) {
  if (pathname.startsWith('/hry/')) {
    return { labelKey: 'nav.gameDetail', fallback: 'Detail hry' };
  }
  for (const section of NAV) {
    for (const item of section.items) {
      if (item.end ? pathname === item.to : pathname.startsWith(item.to)) return item;
    }
  }
  return { labelKey: 'nav.dashboard', fallback: 'Přehled' };
}

function sectionOf(pathname: string) {
  for (const section of NAV) {
    for (const item of section.items) {
      if (item.end ? pathname === item.to : pathname.startsWith(item.to)) return section;
    }
  }
  return NAV[0];
}
